import { useListingsContext } from '@/app/listings/context/ListingsContext';
import { Button } from '@/components/ui';
import { PopoverContext } from '@/components/ui/Popover';
import { cn } from '@/lib/cn';
import { useStore } from '@/providers/StoreProvider';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

export type FilterActionBarScope =
  | 'all'
  | 'location'
  | 'price'
  | 'beds'
  | 'baths'
  | 'moveInDate'
  | 'amenities'
  | 'compliances'
  | {
      kind: 'buildingStatus';
      key: 'isRentStabilized' | 'isVerified' | 'hasBuildingGoodCauseEviction' | 'isTopRated';
    };

export function FilterActionBar({
  scope = 'all',
  showReset = true,
  onReset = () => {},
  onApply = () => {},
  disabled = false,
  primaryButtonText,
  className,
  shouldUpdateUrl = true,
}: {
  scope?: FilterActionBarScope;
  showReset?: boolean;
  onReset?: () => void;
  onApply?: () => void;
  className?: string;
  primaryButtonText?: string;
  disabled?: boolean;
  shouldUpdateUrl?: boolean;
}) {
  const currentFilters = useStore(s => s.listingsFilters);
  const getUrlFilterPayload = useStore(s => s.listingsFilters.getUrlFilterPayload);
  const popOverContext = useContext(PopoverContext);
  const updateListingsCount = useStore(s => s.listingsFilters.updateListingsCount);
  const listingsCount = useStore(s => s.listingsFilters.listingsCount);
  // const [hasReset, setHasReset] = useState(false);
  const snapShot = useRef({ ...currentFilters });
  const [loading, setLoading] = useState(false);
  const isSplitButtons = Boolean(primaryButtonText);
  const { customMap } = useListingsContext() ?? { customMap: false };

  const isResetDisabled = useMemo(() => {
    const isAllNull = (obj: Record<string, unknown>) => Object.values(obj).every(v => v === null);

    const isLocationDefault =
      (currentFilters.neighborhoodIds?.length ?? 0) === 0 && !currentFilters.building?.slug;

    const isPriceDefault = currentFilters.price.min === null && currentFilters.price.max === null;
    const isBedsDefault = (currentFilters.bedrooms?.length ?? 0) === 0;
    const isBathsDefault = currentFilters.minNumBathrooms === null;
    const isMoveInDefault =
      (currentFilters.expectedMoveInDate?.selections?.length ?? 0) === 0 &&
      currentFilters.expectedMoveInDate?.min === null &&
      currentFilters.expectedMoveInDate?.max === null;
    const isAmenitiesDefault = isAllNull(
      currentFilters.amenities as unknown as Record<string, unknown>,
    );
    const isCompliancesDefault = isAllNull(
      currentFilters.compliances as unknown as Record<string, unknown>,
    );
    const isBuildingStatusDefault = isAllNull(
      currentFilters.buildingStatus as unknown as Record<string, unknown>,
    );

    const isAllDefault =
      isLocationDefault &&
      isPriceDefault &&
      isBedsDefault &&
      isBathsDefault &&
      isMoveInDefault &&
      isAmenitiesDefault &&
      isCompliancesDefault &&
      isBuildingStatusDefault;

    const scopeDefault =
      scope === 'all'
        ? isAllDefault
        : scope === 'location'
          ? isLocationDefault
          : scope === 'price'
            ? isPriceDefault
            : scope === 'beds'
              ? isBedsDefault
              : scope === 'baths'
                ? isBathsDefault
                : scope === 'moveInDate'
                  ? isMoveInDefault
                  : scope === 'amenities'
                    ? isAmenitiesDefault
                    : scope === 'compliances'
                      ? isCompliancesDefault
                      : typeof scope === 'object' && scope.kind === 'buildingStatus'
                        ? currentFilters.buildingStatus?.[scope.key] === null
                        : isAllDefault;

    return scopeDefault;
  }, [
    currentFilters.amenities,
    currentFilters.bedrooms,
    currentFilters.building?.slug,
    currentFilters.buildingStatus,
    currentFilters.compliances,
    currentFilters.expectedMoveInDate?.max,
    currentFilters.expectedMoveInDate?.min,
    currentFilters.expectedMoveInDate?.selections,
    currentFilters.minNumBathrooms,
    currentFilters.neighborhoodIds,
    currentFilters.price.max,
    currentFilters.price.min,
    scope,
  ]);

  const fetchListingsCount = async () => {
    if (!shouldUpdateUrl) {
      return;
    }
    setLoading(true);
    await updateListingsCount({ withMapBoundaries: customMap });
    setLoading(false);
  };

  const filtersForCount = useMemo(
    () => ({
      price: currentFilters.price,
      bedrooms: currentFilters.bedrooms,
      minNumBathrooms: currentFilters.minNumBathrooms,
      neighborhoodIds: currentFilters.neighborhoodIds,
      buildingStatus: currentFilters.buildingStatus,
      expectedMoveInDate: currentFilters.expectedMoveInDate,
      compliances: currentFilters.compliances,
      amenities: currentFilters.amenities,
      mapBoundaries: currentFilters.map.boundaries,
      buildingSlugs: currentFilters.building?.slug ? [currentFilters.building.slug] : [],
    }),
    [
      currentFilters.price,
      currentFilters.bedrooms,
      currentFilters.minNumBathrooms,
      currentFilters.neighborhoodIds,
      currentFilters.buildingStatus,
      currentFilters.expectedMoveInDate,
      currentFilters.compliances,
      currentFilters.amenities,
      currentFilters.map.boundaries,
      currentFilters.building?.slug,
    ],
  );

  useEffect(() => {
    fetchListingsCount();
  }, [filtersForCount]);

  const updateUrl = () => {
    if (!shouldUpdateUrl) {
      return;
    }
    const urlPayload = getUrlFilterPayload();
    window.history.replaceState(null, '', urlPayload ? `/listings/${urlPayload}` : '/listings');
  };

  useEffect(() => {
    return () => {
      updateUrl();
    };
  }, []);

  return (
    <div
      className={cn(
        'flex items-center gap-4 md:justify-end justify-center',
        'py-4 px-6 border-t border-secondary',
        className,
      )}
    >
      {showReset && (
        <Button
          variant="tertiary"
          size="sm"
          className={cn(
            isSplitButtons ? 'flex-1 md:flex-none' : '',
            !shouldUpdateUrl && 'min-w-32',
          )}
          disabled={isResetDisabled}
          onClick={() => {
            onReset();
            // setHasReset(true);
            // updateUrl();
          }}
        >
          Reset
        </Button>
      )}
      <Button
        className={cn(
          'flex-1 md:flex-none min-w-32',
          'disabled:bg-secondary disabled:text-secondary-text disabled:text-tertiary disabled:cursor-not-allowed',
        )}
        variant="primary"
        size="sm"
        isLoading={loading}
        disabled={disabled}
        onClick={() => {
          onApply();
          snapShot.current = currentFilters;
          if (shouldUpdateUrl) {
            updateUrl();
          }
          popOverContext?.close?.();
        }}
      >
        {primaryButtonText || `View ${listingsCount?.toLocaleString() ?? ''} listings`}
      </Button>
    </div>
  );
}
